import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { useSnackbar } from 'src/components/snackbar';
import { useEditMetric } from 'src/data/impact/mutation/impact-mutation';
import { useGetUser } from 'src/hooks/useGetUser';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface SubValueRowItemProps {
  v: { key: number; value: string };
  data:any;
  editingKey: number | undefined;
  setEditingKey: React.Dispatch<React.SetStateAction<number | undefined>>;
  editedValues: { [key: number]: string };
  setEditedValues: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
  id: string;
}

const SubValueRowItem: React.FC<SubValueRowItemProps> = ({
  v, 
  data,
  editingKey, 
  setEditingKey, 
  editedValues, 
  setEditedValues,
  id
}) => {
  const user = useGetUser();
  const editMetric = useEditMetric();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);

  const [showToolbar, setShowToolbar] = useState(false);
  const [isFirstClick, setIsFirstClick] = useState(true);
  const quillRef = useRef<ReactQuill>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Function to strip HTML tags
  const stripHtmlTags = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const colorPalette = [
    '#000000', // Black
    '#FFFFFF', // White
    '#FF0000', // Red
    '#00AA55', // Green
    '#0099F2', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
  ];

  const icons = Quill.import('ui/icons');

  // Dynamically create icons for each color
  colorPalette.forEach((color) => {
    icons[color] = `<svg viewBox="0 0 18 18" width="18" height="18">
    <circle cx="9" cy="9" r="7" fill="${color}" stroke="black" stroke-width="1"/>
  </svg>`;
  });

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [['bold', 'italic', ...colorPalette]],
        handlers: {
          color: function (this: any, value: string) {
            const quill = this.quill;
            const range = quill.getSelection();

            if (range) {
              quill.format('color', value);
              setShowToolbar(true);
            }
          },
          ...colorPalette.reduce((handlers, color) => {
            handlers[color] = function (this: any) {
              const quill = this.quill;
              const range = quill.getSelection();

              if (range) {
                quill.format('color', color);
                setShowToolbar(true);
              }
            };
            return handlers;
          }, {} as Record<string, (this: any) => void>),
        },
      },
    }),
    []
  );
  const formats = ['bold', 'italic', 'color'];

  const handleUpdateValue = (key: number) => {
    const index = data.values.findIndex(
        (item: { key: number; value: string }) => item.key == key
      );
  
    if (index !== -1) {
        const newValues = [...data.values];
        newValues[index].value = editedValues[key];
  
      editMetric.mutate(
        {
          values: newValues,
          id,
        },
        {
          onSuccess: (res) => {
            enqueueSnackbar('mis à jour avec succès.');
            setEditingKey(undefined);
            setShowToolbar(false);
            setIsEditing(false);
          },
          onError: (err) => {
            enqueueSnackbar("quelque chose s'est mal passé", {
              variant: 'error',
            });
          },
        }
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quillRef.current && toolbarRef.current) {
        const quillContainer = quillRef.current.getEditor().root;
        const toolbarElement = toolbarRef.current;
        const targetNode = event.target as Node;

        // Find the closest parent container that includes both quill and toolbar
        const editorContainer = quillContainer.closest('.relative');

        // Check if the click is completely outside the editor container
        if (editorContainer && !editorContainer.contains(targetNode)) {
          if (!isFirstClick) {
            setShowToolbar(false);
            setIsEditing(false);
            // Directly call handleUpdateValue when closing
            handleUpdateValue(v.key);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFirstClick, v.key, handleUpdateValue]);

  useEffect(() => {
    if (isEditing) {
      setShowToolbar(true);
    }
  }, [isEditing]);

  useEffect(() => {
    if (editingKey !== undefined) {
      setShowToolbar(true);
    }
  }, [editingKey]);

  const handleEditClick = (key: number, currentValue: string) => {
    if (user && user.role === 'admin') {
      if (isFirstClick) {
        setIsFirstClick(false);
        setEditingKey(key);
        setEditedValues(prev => ({
          ...prev,
          [key]: currentValue || ''
        }));
        setIsEditing(true);
      } else {
        setEditingKey(key);
        setEditedValues(prev => ({
          ...prev,
          [key]: currentValue || ''
        }));
        setIsEditing(true);
      }
    }
  };

 
  return (
    <div
      key={v.key}
      className="flex-grow text-thin text-xs border-r-2 "
    >
      {isEditing && editingKey === v.key && user && user.role === 'admin' ? (
        <div className="relative">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={editedValues[v.key] || ''}
            onChange={(value) => setEditedValues(prev => ({
              ...prev,
              [v.key]: value
            }))}
            onBlur={() => {
                handleUpdateValue(v.key);
              setIsEditing(false);
            }}
            modules={modules}
            formats={formats}
            className="min-w-[60px] w-full text-center custom-quill-editor relative"
          />
          {showToolbar && <div ref={toolbarRef} className="custom-toolbar"></div>}
        </div>
      ) : (
        <Tooltip title={stripHtmlTags(v.value)}>
          <div
            className="w-[60px] pl-2 overflow-hidden text-ellipsis whitespace-wrap flex h-full items-center justify-center cursor-pointer"
            onClick={() => handleEditClick(v.key, v.value)}
            dangerouslySetInnerHTML={{ __html: v.value }}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default SubValueRowItem;